import { useConfig } from '@/composables/game/config'
import { computed, ref } from 'vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  ECOMMERCE_ITEM_TAG,
  MYSTERY_BOX_REWARD_TYPE,
  MYSTERY_BOX_TYPE,
} from '@/types/shared/player-config'
import { usePlayer } from '@/composables/game/player'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import type { MysteryBoxItem } from '@/types/mystery-box'
import DefaultImage from '@/assets/images/mystery-boxes/mystery-boxes-main.webp'
import { useEcommerceItems } from '@/composables/game/ecommerce'
import { useShop } from '@/composables/game/shop'
import { useLocalStorage } from '@vueuse/core'

export const useMysteryBoxes = () => {
  const { config } = useConfig()
  const { player } = usePlayer()
  const { ecommerceItems } = useEcommerceItems()

  const mysteryBoxRewards = computed(() => config.value.game.ecommerce.mysteryBoxRewards)
  const mysteryBoxEcommerceItems = computed(() => ecommerceItems.value.filter(item => item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX)))
  const unopenedMysteryBoxes = computed(() => player.value.ecommerce.mysteryBoxes)
  const availableMysteryBoxTypes = computed(() => ecommerceItems.value.reduce<MYSTERY_BOX_TYPE[]>((acc, item) => {
    const firstBenefit = item.benefits[0]
    if (!firstBenefit || firstBenefit.type !== ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX) return acc
    if (acc.includes(firstBenefit.boxType)) return acc
    return [...acc, firstBenefit.boxType]
  }, []))

  const rewardTypeOrder = [
    MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT, // All except VIRTUAL_FRIEND
    MYSTERY_BOX_REWARD_TYPE.GIVEAWAY_TICKET,
    MYSTERY_BOX_REWARD_TYPE.SKIN,
  ]

  const benefitTypeOrder = [
    ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN,
    ECOMMERCE_ITEM_BENEFIT_TYPE.COIN,
    ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN,
    ECOMMERCE_ITEM_BENEFIT_TYPE.PROFIT_X2,
    ECOMMERCE_ITEM_BENEFIT_TYPE.OFFLINE_PROFIT,
    ECOMMERCE_ITEM_BENEFIT_TYPE.VIRTUAL_FRIEND,
  ]

  const _mysteryBoxes = computed((): MysteryBoxItem[] => {
    return availableMysteryBoxTypes.value.map(boxType => {
      const mysteryBoxType = boxType as MYSTERY_BOX_TYPE
      const ecommerceItems = getEcommerceItemsForMysteryBox(mysteryBoxType)
      const firstItem = ecommerceItems[0]
      if (!firstItem) throw new Error('No ecommerce item found for the box type ' + mysteryBoxType)
      return {
        rewards: mysteryBoxRewards.value[mysteryBoxType],
        boxType: mysteryBoxType,
        name: firstItem.name,
        image: firstItem.imageUrl ?? DefaultImage,
        description: firstItem.description,
        ecommerceItems,
      }
    })
  })

  const canHaveSale = computed(() => _mysteryBoxes.value.some((box) => box.ecommerceItems.some((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT))))

  const { isSale } = useShop()
  const shownMysteryBoxSaleStart = useLocalStorage<number>('shownMysteryBoxSaleStart', 0)

  const shouldRedirectToMysteryBoxSale = computed(() => {
    const sales = config.value.game.ecommerce.sales
    return isSale.value && canHaveSale.value && !!sales && shownMysteryBoxSaleStart.value !== sales.start
  })

  function markMysteryBoxSaleAsShown() {
    const sales = config.value.game.ecommerce.sales
    if (sales) {
      shownMysteryBoxSaleStart.value = sales.start
    }
  }

  function getEcommerceItemsForMysteryBox(boxType: MYSTERY_BOX_TYPE) {
    return mysteryBoxEcommerceItems.value.filter(item => !!(item.benefits.find(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX && b.boxType === boxType)))
  }

  function sortMysteryBoxRewards(mysteryBoxItem: MysteryBoxItem) {
    mysteryBoxItem.rewards = mysteryBoxItem.rewards.sort((a, b) => {
      const typeIndexA = rewardTypeOrder.indexOf(a.type)
      const typeIndexB = rewardTypeOrder.indexOf(b.type)
      if (typeIndexA !== typeIndexB) return typeIndexA - typeIndexB

      // Order within ECOMMERCE_BENEFIT by benefit type
      if (a.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT && b.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT) {
        const benefitIndexA = benefitTypeOrder.indexOf(a.benefit.type)
        const benefitIndexB = benefitTypeOrder.indexOf(b.benefit.type)
        if (benefitIndexA !== benefitIndexB) return benefitIndexA - benefitIndexB
      }

      // Order within groups by amount (if applicable)
      const amountA = 'amount' in a ? a.amount : ('benefit' in a ? a.benefit?.amount ?? 0 : 0)
      const amountB = 'amount' in b ? b.amount : ('benefit' in b ? b.benefit?.amount ?? 0 : 0)
      return amountA - amountB
    })
    return mysteryBoxItem
  }

  return {
    mysteryBoxRewards,
    getEcommerceItemsForMysteryBox,
    unopenedMysteryBoxes,
    availableMysteryBoxTypes,
    sortMysteryBoxRewards,
    _mysteryBoxes,
    canHaveSale,
    shouldRedirectToMysteryBoxSale,
    markMysteryBoxSaleAsShown,
  }
}

export const useOpenMysteryBox = () => {
  const { notify } = useToaster()
  const { updatePlayer } = usePlayer()

  const openingBoxType = ref<null | MYSTERY_BOX_TYPE>(null)

  async function openBox(boxType: MYSTERY_BOX_TYPE) {
    openingBoxType.value = boxType
    try {
      const { rewards, playerStats } = await api.openMysteryBox({ boxType })
      setTimeout(() => updatePlayer(playerStats), 2000)
      return rewards
    } catch (e) {
      notify({ type: 'error', title: 'Could not open, try again!' })
      await api.reportError({
        code: 'could_not_open_mystery_box',
        metadata: { boxType },
        description: 'Could not open the mystery box',
      })
      return null
    } finally {
      openingBoxType.value = null
    }
  }

  return {
    openBox,
    openingBoxType,
  }
}
